import { useSearch } from '@tanstack/react-router'
import { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/DocumentTableColumns'
import {
  EmbeddingResult,
  ParsedCantileverAiQueryResult,
  useRolodexAi,
} from 'features/rolodex'
import { useCallback, useMemo, useState } from 'react'
import { RolodexEntryType } from 'types'

export interface SearchConfig {
  chunk_max_score_weight: number
  chunk_count_weight: number
  title_lexical_score_weight: number
}

interface UseDocumentSemanticSearchResult {
  searchResults: DocumentTableRow[]
  isLoading: boolean
  error: Error | null
  search: (query: string) => Promise<void>
  clearResults: () => void
  config: SearchConfig
}

const DEFAULT_TOP_K = 500
const DEFAULT_TOP_SCORE_WEIGHT = 0.2
const DEFAULT_CHUNK_COUNT_WEIGHT = 0.2
const DEFAULT_TITLE_LEXICAL_SCORE_WEIGHT = 1

function splitQueryIntoKeywords(query: string): string[] {
  return query
    .toLowerCase()
    .split(/[^a-zA-Z0-9]+/)
    .filter((keyword) => keyword.length > 0)
}

function calculateTitleLexicalScore(
  documentTitle: string | null,
  keywords: string[]
): number {
  if (!documentTitle || keywords.length === 0) {
    return 0
  }

  const lowerTitle = documentTitle.toLowerCase()
  let matchCount = 0

  for (const keyword of keywords) {
    if (lowerTitle.includes(keyword)) {
      matchCount += 1
    }
  }

  return Math.log(1 + matchCount)
}

interface EntryScoreData {
  maxScore: number
  chunkCount: number
  titleLexicalScore: number
}

function computeCombinedScore(
  maxScore: number,
  chunkCount: number,
  titleLexicalScore: number,
  config: SearchConfig
): number {
  const topScoreWeight = config.chunk_max_score_weight
  const chunkCountWeight = config.chunk_count_weight
  const titleLexicalScoreWeight = config.title_lexical_score_weight
  return (
    topScoreWeight * maxScore +
    chunkCountWeight * Math.log(1 + chunkCount) +
    titleLexicalScoreWeight * titleLexicalScore
  )
}

function getRankedEntryIds(
  query: string,
  results: EmbeddingResult[],
  allDocuments: DocumentTableRow[],
  config: SearchConfig
): string[] {
  const entryScores = new Map<string, EntryScoreData>()
  const keywords = splitQueryIntoKeywords(query)

  for (const doc of allDocuments) {
    const titleLexicalScore = calculateTitleLexicalScore(
      doc.documentTitle,
      keywords
    )
    entryScores.set(doc.id, {
      maxScore: 0,
      chunkCount: 0,
      titleLexicalScore,
    })
  }

  for (const result of results) {
    const entryId = result.metadata.entry_id
    const existing = entryScores.get(entryId)

    if (existing) {
      existing.maxScore = Math.max(existing.maxScore, result.score)
      existing.chunkCount += 1
    }
  }

  const rankedEntries = Array.from(entryScores.entries())
    .map(([entryId, data]) => ({
      entryId,
      combinedScore: computeCombinedScore(
        data.maxScore,
        data.chunkCount,
        data.titleLexicalScore,
        config
      ),
      ...data,
    }))
    .filter(({ combinedScore }) => combinedScore > 0)
    .sort((a, b) => b.combinedScore - a.combinedScore)
  return rankedEntries.map(({ entryId }) => entryId)
}

export function useDocumentSemanticSearch(
  siteId: string,
  allDocuments: DocumentTableRow[],
  topK: number = DEFAULT_TOP_K
): UseDocumentSemanticSearchResult {
  const [searchResults, setSearchResults] = useState<DocumentTableRow[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const searchParams = useSearch({ strict: false }) as Partial<SearchConfig>
  const queryRolodexAi = useRolodexAi()

  const config = useMemo(
    () => ({
      chunk_max_score_weight:
        searchParams.chunk_max_score_weight ?? DEFAULT_TOP_SCORE_WEIGHT,
      chunk_count_weight:
        searchParams.chunk_count_weight ?? DEFAULT_CHUNK_COUNT_WEIGHT,
      title_lexical_score_weight:
        searchParams.title_lexical_score_weight ??
        DEFAULT_TITLE_LEXICAL_SCORE_WEIGHT,
    }),
    [searchParams]
  )

  const search = useCallback(
    async (query: string) => {
      if (!query.trim()) {
        setSearchResults([])
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const result: ParsedCantileverAiQueryResult = await queryRolodexAi({
          query,
          load_options: {
            type: RolodexEntryType.SYSTEM_DOCUMENT,
            tags: { site: siteId },
            limit: topK,
          },
          top_k: topK,
          re_rank: true,
          include_embeddings: false,
        })

        const orderedEntryIds = getRankedEntryIds(
          query,
          result.results,
          allDocuments,
          config
        )
        const documentMap = new Map(allDocuments.map((doc) => [doc.id, doc]))
        const tableRows = orderedEntryIds
          .map((id) => documentMap.get(id))
          .filter((doc): doc is DocumentTableRow => doc !== undefined)
        setSearchResults(tableRows.slice(0, topK))
      } catch (err) {
        const errorObj = err instanceof Error ? err : new Error(String(err))
        setError(errorObj)
        setSearchResults([])
      } finally {
        setIsLoading(false)
      }
    },
    [queryRolodexAi, siteId, topK, config, allDocuments]
  )

  const clearResults = useCallback(() => {
    setSearchResults([])
    setError(null)
  }, [])

  return {
    searchResults,
    isLoading,
    error,
    search,
    clearResults,
    config,
  }
}
